import type { Abi } from 'viem';
import { PublicClient } from 'viem';

import {
  defaultsForChain,
  isRetryableError,
  ReadRetryOptions,
  sleep,
} from 'shared/utils/retryUtils';

export const readContractCall = async <T>(
  provider: PublicClient,
  address: `0x${string}`,
  abi: Abi | readonly unknown[],
  functionName: string,
  args: Parameters<PublicClient['readContract']>[0]['args'] = [],
  options: ReadRetryOptions<T> = {},
): Promise<T> => {
  const base = defaultsForChain(provider);
  const attempts = options.attempts ?? base.attempts;
  const backoff = options.backoff ?? base.backoff;
  const jitterMs = options.jitterMs ?? base.jitterMs;
  const initialDelay = options.delayMs ?? base.delayMs;

  const clients: PublicClient[] = [provider, ...(options.fallbacks ?? [])];

  let lastErr: unknown;

  for (const client of clients) {
    let delay = initialDelay;

    for (let i = 0; i < attempts; i++) {
      try {
        const result = (await client.readContract({
          address,
          abi,
          functionName,
          args,
          authorizationList: undefined,
        })) as T;

        if (options.validate && !options.validate(result)) {
          // treat as retryable "empty/stale" read
          throw new Error('ValidationFailed');
        }

        return result;
      } catch (error) {
        lastErr = error;

        // Non-retryable? bubble up immediately.
        if (!isRetryableError(error) && (error as any)?.message !== 'ValidationFailed') {
          break;
        }

        // If this was our last try on this client, break to next client.
        const lastTryOnThisClient = i === attempts - 1;
        if (lastTryOnThisClient) break;

        // sleep with jitter, then back off
        const jitter = Math.floor((Math.random() - 0.5) * 2 * jitterMs);
        await sleep(Math.max(0, delay + jitter));
        delay = Math.floor(delay * backoff);
      }
    }
  }

  // include the original function name and last error message for easier telemetry
  const msg = (lastErr as any)?.message ? `: ${(lastErr as any).message}` : '';
  throw new Error(`Failed to read ${functionName}${msg}`);
};

export type MulticallResult<T> =
  | { status: 'success'; result: T }
  | { status: 'failure'; error?: unknown };

export type ContractCall<TArgs extends readonly any[] = any[], TResult = any> = {
  address: `0x${string}`;
  abi: Abi;
  functionName: string;
  args?: TArgs;
};

export type MulticallOptions = {
  attempts?: number;
  delayMs?: number;
  backoff?: number;
  jitterMs?: number;
  fallbacks?: PublicClient[];
};

export async function readMulticall(
  provider: PublicClient,
  calls: { address: `0x${string}`; abi: Abi; functionName: string; args?: readonly any[] }[],
  options: MulticallOptions = {},
): Promise<MulticallResult<any>[]> {
  const base = defaultsForChain(provider);
  const attempts = options.attempts ?? base.attempts;
  const backoff = options.backoff ?? base.backoff;
  const jitterMs = options.jitterMs ?? base.jitterMs;
  const initialDelay = options.delayMs ?? base.delayMs;

  const clients: PublicClient[] = [provider, ...(options.fallbacks ?? [])];
  let lastErr: unknown;

  for (const client of clients) {
    let delay = initialDelay;

    for (let i = 0; i < attempts; i++) {
      try {
        //@ts-ignore
        const results = (await client.multicall({
          allowFailure: true,
          contracts: calls as any,
        })) as any[];

        return results.map((r) => {
          if (r?.status === 'success' && r.result != null) {
            return { status: 'success' as const, result: r.result };
          } else {
            return { status: 'failure' as const, error: r?.error };
          }
        });
      } catch (err) {
        lastErr = err;

        if (!isRetryableError(err)) break;
        if (i === attempts - 1) break;

        const jitter = Math.floor((Math.random() - 0.5) * 2 * jitterMs);
        await sleep(Math.max(0, delay + jitter));
        delay = Math.floor(delay * backoff);
      }
    }
  }

  const msg = (lastErr as any)?.message ? `: ${(lastErr as any).message}` : '';
  throw new Error(`Failed multicall${msg}`);
}
